import React, { useEffect, useState } from 'react';
import { UserRole, Branch } from '../../types/db';
import { fetchUsers, createUser, updateUser, deleteUser } from '../../services/userService';
import {
  Plus,
  Trash2,
  Loader2,
  X,
  Edit3,
  ShieldCheck,
  RefreshCcw,
  Building2,
  UserX,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export function UserManager({
  currentUserRole,
  branches,
}: {
  currentUserRole: UserRole;
  branches: Branch[];
}) {
  const { profile: me } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: UserRole.ACADEMIC,
    branchId: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    else setIsRefreshing(true);
    try {
      const uData = await fetchUsers();
      setUsers(uData.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '')));
    } catch (err: any) {
      /* console removed */
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const visibleUsers = users.filter((u) => {
    if (currentUserRole === UserRole.MASTER_ADMIN) return true;
    if (currentUserRole === UserRole.SUPER_ADMIN) {
      return u.role !== UserRole.MASTER_ADMIN;
    }
    return false;
  });

  const handleOpenAdd = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      role: UserRole.ACADEMIC,
      branchId: branches.length > 0 ? branches[0].id : '',
    });
    setIsFormOpen(true);
  };

  const handleOpenEdit = (user: any) => {
    if (
      currentUserRole === UserRole.SUPER_ADMIN &&
      (user.role === UserRole.MASTER_ADMIN || user.role === UserRole.SUPER_ADMIN) &&
      user.id !== me?.id
    ) {
      return window.alert(
        'Otoritas Ditolak: Anda tidak memiliki akses untuk mengelola akun level setara atau di atas Anda.'
      );
    }
    setEditingUser(user);
    setFormData({
      email: user.email || '',
      password: '',
      name: user.name,
      role: user.role as UserRole,
      branchId: user.branch_id || '',
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (editingUser) {
        await updateUser({
          user_id: editingUser.id,
          name: formData.name,
          role: formData.role,
          branch_id: formData.branchId || null,
        });
        window.alert('Data admin berhasil diperbarui!');
      } else {
        await createUser({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          role: formData.role,
          branch_id: formData.branchId,
        });
        window.alert('Akun admin baru berhasil diaktifkan!');
      }
      setIsFormOpen(false);
      await loadData(true);
    } catch (err: any) {
      window.alert('Gagal: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (user: any) => {
    if (user.id === me?.id) {
      return window.alert('Otoritas Ditolak: Anda tidak bisa menghapus akun Anda sendiri.');
    }

    if (currentUserRole === UserRole.SUPER_ADMIN) {
      if (user.role === UserRole.MASTER_ADMIN || user.role === UserRole.SUPER_ADMIN) {
        return window.alert(
          'Otoritas Ditolak: Super Admin dilarang menghapus akun level setara atau di atasnya.'
        );
      }
    }

    if (!window.confirm(`Hapus seluruh akses sistem untuk ${user.name}? Tindakan ini permanen.`))
      return;

    try {
      setIsRefreshing(true);
      await deleteUser(user.id);
      await loadData(true); // <-- PERBAIKAN: Tambah await agar UI sinkron
      window.alert('User berhasil dihapus.');
    } catch (err: any) {
      window.alert('Error: ' + err.message);
      setIsRefreshing(false);
    }
  };

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center p-20 gap-4">
        <Loader2 className="animate-spin text-indigo-600 w-10 h-10" />
        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400">
          Security Syncing...
        </p>
      </div>
    );

  return (
    <div className="space-y-6 animate-fade-in pb-20 font-sans">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-black text-slate-900 tracking-tight uppercase">
            User Authority Management
          </h1>
          <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
            Level Akses Anda:{' '}
            {currentUserRole === UserRole.MASTER_ADMIN ? 'Full (Master)' : 'Operational (Super)'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => loadData(true)}
            className="p-2.5 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-indigo-600 transition-all"
          >
            <RefreshCcw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleOpenAdd}
            className="bg-indigo-600 text-white px-4 py-2 rounded-xl font-black uppercase text-[9px] tracking-widest shadow-md hover:bg-indigo-700 transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Tambah Admin
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 border-b text-slate-400 font-black uppercase tracking-widest text-[8px]">
              <tr>
                <th className="p-5 text-left">Admin Identity</th>
                <th className="p-5 text-left">Authority Level</th>
                <th className="p-5 text-left">Unit Tugas</th>
                <th className="p-5 text-center w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {visibleUsers.map((u: any) => {
                const isMe = u.id === me?.id;
                const canBeDeleted =
                  !isMe &&
                  (currentUserRole === UserRole.MASTER_ADMIN ||
                    (currentUserRole === UserRole.SUPER_ADMIN &&
                      u.role !== UserRole.SUPER_ADMIN &&
                      u.role !== UserRole.MASTER_ADMIN));

                return (
                  <tr key={u.id} className="hover:bg-slate-50 transition-colors group">
                    <td className="p-5">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 ${u.role === UserRole.MASTER_ADMIN ? 'bg-slate-900 text-white' : 'bg-indigo-50 text-indigo-600'} rounded-xl flex items-center justify-center font-black text-xs uppercase shadow-inner`}
                        >
                          {u.name?.charAt(0)}
                        </div>
                        <div>
                          <div className="font-black text-slate-900 text-xs uppercase tracking-tight flex items-center gap-2">
                            {u.name}{' '}
                            {isMe && (
                              <span className="text-[7px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-md">
                                YOU
                              </span>
                            )}
                          </div>
                          <div className="text-[8px] text-slate-400 font-mono">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-5">
                      <span
                        className={`px-3 py-1 rounded-lg text-[7px] font-black uppercase tracking-tighter ${
                          u.role === UserRole.MASTER_ADMIN
                            ? 'bg-slate-900 text-white'
                            : u.role === UserRole.SUPER_ADMIN
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-indigo-50 text-indigo-700'
                        }`}
                      >
                        {u.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-5">
                      <div className="text-[10px] font-black text-slate-500 uppercase flex items-center gap-1.5">
                        <Building2 className="w-3 h-3 text-slate-300" />{' '}
                        {u.branch_id || 'KANTOR PUSAT'}
                      </div>
                    </td>
                    <td className="p-5 text-center">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleOpenEdit(u)}
                          className="text-slate-400 hover:text-indigo-600 p-2 transition-all hover:bg-indigo-50 rounded-lg"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>

                        {canBeDeleted ? (
                          <button
                            onClick={() => handleDelete(u)}
                            className="text-slate-400 hover:text-rose-600 p-2 transition-all hover:bg-rose-50 rounded-lg"
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <div className="p-2 text-slate-200 cursor-not-allowed">
                            <UserX className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {isFormOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[150] flex items-center justify-center p-4 backdrop-blur-md overflow-y-auto no-scrollbar">
          <div className="bg-white rounded-[3rem] w-full max-w-sm p-10 shadow-2xl animate-fade-in-up border border-white relative">
            <button
              onClick={() => setIsFormOpen(false)}
              className="absolute top-8 right-8 text-slate-300 hover:text-slate-900 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center mb-8">
              <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <h3 className="font-black text-xl text-slate-900 uppercase tracking-tight">
                Setup Authority
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <label className="text-[8px] font-black text-indigo-600 uppercase ml-2">
                  Nama Lengkap
                </label>
                <input
                  className="w-full border-2 border-slate-50 p-4 rounded-2xl outline-none font-bold bg-slate-50/50 text-xs focus:border-indigo-200"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              {!editingUser && (
                <>
                  <div className="space-y-1">
                    <label className="text-[8px] font-black text-indigo-600 uppercase ml-2">
                      Email Login
                    </label>
                    <input
                      className="w-full border-2 border-slate-50 p-4 rounded-2xl outline-none font-mono bg-slate-50/50 text-xs"
                      placeholder="admin@delta.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[8px] font-black text-indigo-600 uppercase ml-2">
                      Initial Password
                    </label>
                    <input
                      className="w-full border-2 border-slate-50 p-4 rounded-2xl outline-none bg-slate-50/50 text-xs"
                      type="text"
                      placeholder="Set password..."
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                  </div>
                </>
              )}
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-1">
                  <label className="text-[8px] font-black text-indigo-600 uppercase ml-2">
                    Pilih Role
                  </label>
                  <select
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl outline-none font-black bg-slate-50/50 text-[10px]"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                  >
                    {currentUserRole === UserRole.MASTER_ADMIN && (
                      <option value={UserRole.SUPER_ADMIN}>SUPER ADMIN</option>
                    )}
                    <option value={UserRole.CENTRAL_ADMIN}>CENTRAL ADMIN</option>
                    <option value={UserRole.ACADEMIC}>ACADEMIC ADMIN</option>
                    <option value={UserRole.FINANCE}>FINANCE ADMIN</option>
                    <option value={UserRole.TENTOR}>TENTOR</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[8px] font-black text-indigo-600 uppercase ml-2">
                    Lokasi Penugasan
                  </label>
                  <select
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl outline-none font-black bg-slate-50/50 text-[10px]"
                    value={formData.branchId}
                    onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                  >
                    <option value="">KANTOR PUSAT (HQ)</option>
                    {branches
                      .filter((b) => b.is_active !== false || b.id === formData.branchId)
                      .map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name.toUpperCase()}
                          {b.is_active === false ? ' (NONAKTIF)' : ''}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <button
                disabled={isSubmitting}
                className="w-full bg-indigo-600 text-white py-5 rounded-3xl font-black uppercase tracking-widest shadow-xl flex justify-center items-center gap-3 hover:bg-indigo-700 transition-all mt-4 text-[9px]"
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin w-4 h-4" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                {editingUser ? 'SAVE CHANGES' : 'ACTIVATE ACCOUNT'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}